import { createContext, lazy, Suspense, useContext, useEffect, useRef, useState } from 'react';
import { NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signInWithEmailAndPassword, signInWithPopup, signOut } from 'firebase/auth';
import { collection, doc, getDoc, onSnapshot, setDoc } from './fs';
import { auth, db, google, USE_EMULATORS } from './firebase';
import { CLINICAS, GRUPO, fijarClinicas } from './clinicas';
import { fijarOrg, infoOrg } from './org';
import { fuenteReglasExtra } from './banco';
import Alta, { MARCA, PanelMarca, LogoGoogle } from './Alta.jsx';
import PrimerosPasos, { usePasos } from './pages/PrimerosPasos.jsx';
import Icono from './Iconos.jsx';
import Fallo from './Fallo.jsx';
// Cada pantalla se descarga solo cuando se abre (el panel arranca mucho más rápido en el móvil)
const Facturas = lazy(() => import('./pages/Facturas.jsx'));
const WhatsApp = lazy(() => import('./pages/WhatsApp.jsx'));
const Resumen = lazy(() => import('./pages/Secciones.jsx').then((m) => ({ default: m.Resumen })));
const Dinero = lazy(() => import('./pages/Secciones.jsx').then((m) => ({ default: m.Dinero })));
const Impuestos = lazy(() => import('./pages/Impuestos.jsx'));
const Pedidos = lazy(() => import('./pages/Pedidos.jsx'));
const Usuarios = lazy(() => import('./pages/Usuarios.jsx'));
const StockEquipo = lazy(() => import('./pages/Stock.jsx').then((m) => ({ default: m.StockEquipo })));
const Asistente = lazy(() => import('./pages/Asistente.jsx'));
const Hospitalizacion = lazy(() => import('./pages/Hospitalizacion.jsx'));
const Hoy = lazy(() => import('./pages/Hoy.jsx'));
const Cirugias = lazy(() => import('./pages/Cirugias.jsx'));
const Configurar = lazy(() => import('./pages/Configurar.jsx'));
const Plataforma = lazy(() => import('./pages/Plataforma.jsx'));
const MiClinica = lazy(() => import('./pages/MiClinica.jsx'));
import Pendientes, { necesitaAccion, usePendientes } from './pages/Pendientes.jsx';
import DiaADia, { useAvisosDia } from './pages/DiaADia.jsx';
import Igualas, { useImpagosIgualas } from './pages/Igualas.jsx';
import Personal, { usePendientesVacaciones } from './pages/Personal.jsx';
import Avisador from './Avisador.jsx';
import Ayuda from './Ayuda.jsx';
import Buscador from './Buscador.jsx';
import { activarAvisos, avisosActivos, desactivarAvisos, esIOS, instalada, refrescarAvisos, registrarSW } from './push.js';
import { useColeccion } from './dia/comun';
import { tomasPendientes } from './hosp/logica';

const GRUPO_COLOR_DEF = '#6254e8';
// Reglas propias de banco de la organización (Mi clínica): se aplican antes que las generales
fuenteReglasExtra(() => infoOrg().reglasBancoExtra || []);


function LoginEmulador() {
  // AUD-LOGIN-01: las cuentas ficticias salen de env/emulator/.env.emulator; un build de producción no las contiene
  const EMU_EMAIL = import.meta.env.VITE_EMU_EMAIL || '';
  const EMU_PASS = import.meta.env.VITE_EMU_PASS || '';
  const [email, setEmail] = useState(EMU_EMAIL);
  const [pass, setPass] = useState(EMU_PASS);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const entrar = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr('');
    try {
      await signInWithEmailAndPassword(auth, email.trim(), pass);
    } catch (ex) {
      setErr(ex?.message || 'No se pudo entrar en el emulador');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={entrar} style={{ display: 'grid', gap: '.6rem', textAlign: 'left' }}>
      <label>Email (ficticio)<input className="campo" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" /></label>
      <label>Contraseña<input className="campo" type="password" value={pass} onChange={(e) => setPass(e.target.value)} autoComplete="current-password" /></label>
      {err && <p className="aviso-txt" role="alert">{err}</p>}
      <button className="btn" type="submit" disabled={busy}>{busy ? 'Entrando…' : 'Entrar (emulador)'}</button>
      {EMU_EMAIL && <p className="tenue" style={{ margin: 0, fontSize: '.8rem' }}>Cuenta ficticia del emulador: {EMU_EMAIL}</p>}
    </form>
  );
}

// ¿Puede usar el panel? (prueba vigente, suscripción activa o cuenta interna)
export const DIAS_GRACIA_IMPAGO = 7;
export function orgActiva(o = infoOrg()) {
  if (o.estado === 'activa' || o.estado === 'interno') return true;
  if (o.estado === 'prueba') return !o.pruebaHasta || o.pruebaHasta > new Date().toISOString();
  // Pago fallido: 7 días de margen mientras Stripe reintenta el cobro
  if (o.estado === 'impago') return !!o.impagoDesde && Date.now() - new Date(o.impagoDesde).getTime() < DIAS_GRACIA_IMPAGO * 864e5;
  return false;
}
// Qué plan necesita cada pantalla (en prueba se ve todo)
export const PLAN_DE = { '/whatsapp': 'whatsapp', '/': 'gestion', '/facturas': 'gestion', '/banco': 'gestion', '/impuestos': 'gestion', '/compras': 'gestion', '/igualas': 'gestion' };
export const NOMBRE_PLAN = { gestion: 'Gestión', whatsapp: 'WhatsApp con IA', completo: 'Completo' };
export function incluye(path, o = infoOrg()) {
  const falta = PLAN_DE[path];
  if (!falta || !['activa', 'impago'].includes(o.estado) || !o.plan || o.plan === 'completo') return true;
  return o.plan === falta;
}
function Mejorar({ path, esAdmin }) {
  const plan = PLAN_DE[path];
  return (
    <section>
      <h1>Incluido en otro plan</h1>
      <div className="tarjeta">
        <p style={{ marginTop: 0 }}>Esta parte del panel está en el plan <b>{NOMBRE_PLAN[plan]}</b> y en el <b>Completo</b>. Tu plan ahora es <b>{NOMBRE_PLAN[infoOrg().plan] || '—'}</b>.</p>
        {esAdmin ? <NavLink className="btn" to="/mi-clinica?tab=suscripcion">Ver planes</NavLink> : <p className="tenue" style={{ margin: 0 }}>Pídeselo a quien administra el panel en tu clínica.</p>}
      </div>
    </section>
  );
}
const diasPrueba = (o) => (o.estado === 'prueba' && o.pruebaHasta ? Math.max(0, Math.ceil((new Date(o.pruebaHasta) - Date.now()) / 864e5)) : null);
const Ctx = createContext(null);
export const usePanel = () => useContext(Ctx);

// Menú por secciones (encargo 3/11/2026): Hoy · Recepción · Clínica · Gestión · Finanzas · Equipo · Configuración.
// "g" = sección; "movil" = va en la barra de abajo del móvil (con su nombre corto). Sin emojis en el menú.
const MODULOS = [
  { g: 'Hoy', path: '/hoy', label: 'Hoy', icono: 'hoy', el: <Hoy />, movil: 'Hoy', desc: 'Lo que hay que hacer ahora en la clínica' },
  { g: 'Recepción', path: '/whatsapp', label: 'WhatsApp', icono: 'whatsapp', el: <WhatsApp />, movil: 'WhatsApp', desc: 'Bandeja, recordatorios y bot' },
  { g: 'Recepción', path: '/dia', label: 'Día a día', icono: 'dia', el: <DiaADia />, avisoDia: true, movil: 'Día', desc: 'Llamadas, notas, caja y registros' },
  { g: 'Recepción', path: '/pendientes', label: 'Pruebas y encargos', icono: 'resultados', el: <Pendientes />, aviso: true, desc: 'Laboratorio y pedidos de clientes' },
  { g: 'Clínica', path: '/hospitalizacion', label: 'Hospitalización', icono: 'hospital', el: <Hospitalizacion />, avisoHosp: true, movil: 'Hospital', desc: 'Ingresados, tomas y altas' },
  { g: 'Clínica', path: '/cirugias', label: 'Cirugías', icono: 'tijera', el: <Cirugias />, desc: 'Agenda de quirófano, prequirúrgicos y ayunos' },
  { g: 'Clínica', path: '/asistente', label: 'Herramientas clínicas', icono: 'asistente', el: <Asistente />, desc: 'Casos, dosis, calculadoras y protocolos' },
  { g: 'Gestión', path: '/compras', label: 'Compras y stock', icono: 'carro', el: <Pedidos />, desc: 'Presupuesto de pedidos y stock' },
  { g: 'Gestión', path: '/igualas', label: 'Igualas', icono: 'iguala', el: <Igualas />, avisoIg: true, desc: 'Planes de salud y cobros' },
  { g: 'Finanzas', path: '/', label: 'Resumen', icono: 'inicio', el: <Resumen />, fijo: true, desc: 'Cómo va el negocio, rentabilidad y ventas' },
  { g: 'Finanzas', path: '/facturas', label: 'Facturas', icono: 'facturas', el: <Facturas />, desc: 'Facturas de proveedores y gestoría' },
  { g: 'Finanzas', path: '/banco', label: 'Banco y caja', icono: 'banco', el: <Dinero />, desc: 'Saldo, previsión de pagos y movimientos' },
  { g: 'Finanzas', path: '/impuestos', label: 'Impuestos', icono: 'impuestos', el: <Impuestos />, desc: 'IVA, modelos, aplazamientos y gestoría' },
  { g: 'Equipo', path: '/personal', label: 'Horarios y vacaciones', icono: 'calendario', el: <Personal />, avisoVac: true, desc: 'Cuadrante y vacaciones' },
];
// Lo que ve el equipo (sin números). Entran en «Hoy».
const MODULOS_EQUIPO = [
  { g: 'Hoy', path: '/', label: 'Hoy', icono: 'hoy', el: <Hoy />, movil: 'Hoy', fijo: true, desc: 'Lo que hay que hacer ahora en la clínica' },
  { g: 'Recepción', path: '/whatsapp', label: 'WhatsApp', icono: 'whatsapp', el: <WhatsApp />, movil: 'WhatsApp', desc: 'Bandeja y recordatorios' },
  { g: 'Recepción', path: '/dia', label: 'Día a día', icono: 'dia', el: <DiaADia />, avisoDia: true, movil: 'Día', desc: 'Llamadas, notas y registros' },
  { g: 'Recepción', path: '/pendientes', label: 'Pruebas y encargos', icono: 'resultados', el: <Pendientes />, aviso: true, desc: 'Laboratorio y pedidos de clientes' },
  { g: 'Clínica', path: '/hospitalizacion', label: 'Hospitalización', icono: 'hospital', el: <Hospitalizacion />, avisoHosp: true, movil: 'Hospital', desc: 'Ingresados, tomas y altas' },
  { g: 'Clínica', path: '/cirugias', label: 'Cirugías', icono: 'tijera', el: <Cirugias />, desc: 'Agenda de quirófano, prequirúrgicos y ayunos' },
  { g: 'Clínica', path: '/asistente', label: 'Herramientas clínicas', icono: 'asistente', el: <Asistente />, desc: 'Casos, dosis, calculadoras y protocolos' },
  // El equipo no tiene Igualas ni compras con precios (AUD-SEC-01): solo cantidades de stock
  { g: 'Gestión', path: '/stock', label: 'Stock', icono: 'carro', el: <StockEquipo />, desc: 'Lo que hay y lo que falta pedir' },
  { g: 'Equipo', path: '/personal', label: 'Horarios y vacaciones', icono: 'calendario', el: <Personal />, desc: 'Cuadrante y vacaciones' },
];
export const modulosDeRol = (rol) => (rol === 'equipo' ? MODULOS_EQUIPO : MODULOS);
const MODULO_ACCESOS = { g: 'Configuración', path: '/accesos', label: 'Usuarios y permisos', icono: 'equipo', el: <Usuarios />, desc: 'Quién entra y qué ve' };
const MODULO_MICLINICA = { g: 'Configuración', path: '/mi-clinica', label: 'Mi clínica', icono: 'ajustes', el: <MiClinica />, desc: 'Clínicas, conexiones y suscripción' };
const TODAS_LAS_RUTAS = new Set([...MODULOS, ...MODULOS_EQUIPO, MODULO_ACCESOS, MODULO_MICLINICA].map((m) => m.path).concat(['/primeros-pasos', '/plataforma', '/configurar']));

function SinSuscripcion({ esAdmin }) {
  const o = infoOrg();
  return (
    <section>
      <h1>{o.estado === 'prueba' ? 'Se ha acabado la prueba' : o.estado === 'baja' ? 'Suscripción cancelada' : 'Suscripción pendiente'}</h1>
      <div className="tarjeta">
        {esAdmin
          ? <><p style={{ marginTop: 0 }}>Para seguir usando el panel (y que el bot y los recordatorios sigan funcionando), activa la suscripción. Tus datos siguen guardados.</p><NavLink className="btn" to="/mi-clinica?tab=suscripcion">Activar suscripción</NavLink></>
          : <p style={{ margin: 0 }}>La suscripción de la clínica no está activa. Avisa a quien administra el panel en tu clínica.</p>}
      </div>
    </section>
  );
}
// Direcciones antiguas → nuevas (marcadores guardados, enlaces de avisos)
function Cargando({ lento }) {
  return (
    <div className="centro" role="status" aria-live="polite">
      <p>Cargando…</p>
      {lento && <p className="tenue" style={{ fontSize: '.9rem' }}>Está tardando más de lo normal. <button className="btn sec mini" onClick={() => window.location.reload()}>Volver a intentarlo</button></p>}
    </div>
  );
}
function Reintentar({ texto, onReintentar }) {
  return (
    <div className="centro login" role="alert">
      <h2>{texto}</h2>
      <p className="tenue">Revisa la conexión y vuelve a intentarlo. Tus datos están a salvo.</p>
      <div className="acciones"><button className="btn" onClick={onReintentar}>Reintentar</button><button className="btn sec" onClick={() => signOut(auth)}>Salir</button></div>
    </div>
  );
}
// AUD-ROL-01: una ruta sin permiso o inexistente muestra un mensaje en lugar de una pantalla en blanco
function SinPermiso({ existe }) {
  return (
    <section>
      <h1>{existe ? 'No tienes acceso a esta sección' : 'Esta página no existe'}</h1>
      <div className="tarjeta">
        <p style={{ marginTop: 0 }}>{existe ? 'Tu usuario no tiene permiso para ver esta parte del panel. Si la necesitas, pídeselo a quien administra el panel en tu clínica.' : 'Puede que el enlace esté mal escrito o que la sección haya cambiado de sitio.'}</p>
        <NavLink className="btn" to="/">Ir al inicio</NavLink>
      </div>
    </section>
  );
}

const ANTIGUAS = { '/ventas': '/?tab=ventas', '/rentabilidad': '/?tab=rentabilidad', '/tesoreria': '/banco', '/pedidos': '/compras', '/recordatorios': '/whatsapp?tab=recordatorios' };
function Redirige({ a }) {
  const ir = useNavigate();
  useEffect(() => { ir(a, { replace: true }); }, [a]);
  return null;
}

// Personalizar menú: cada uno oculta lo que no usa; un administrador puede ocultarlo para todos
function Ajustes({ modulos, mios, todos, inicio, esAdmin, user, perfil, onCerrar }) {
  const uid = user.uid;
  const [avisos, setAvisos] = useState(avisosActivos());
  const [estadoAv, setEstadoAv] = useState('');
  const MSG = { 'instalar-ios': `En iPhone los avisos solo funcionan con el panel instalado: en Safari pulsa Compartir → «Añadir a pantalla de inicio», abre ${MARCA} desde el icono y actívalos ahí.`, denegado: 'El navegador tiene los avisos bloqueados para el panel. Actívalos en los ajustes del navegador (icono del candado junto a la dirección).', 'no-soportado': 'Este navegador no admite avisos.' };
  const alternarAvisos = async () => {
    if (avisos) { await desactivarAvisos(); setAvisos(false); setEstadoAv(''); return; }
    setEstadoAv('Activando…');
    const r = await activarAvisos(user, perfil);
    setAvisos(r === 'ok'); setEstadoAv(r === 'ok' ? 'Listo. Prueba: te llegará un aviso con la próxima urgencia o resultado.' : MSG[r] || r);
  };
  const guardarMios = (l) => setDoc(doc(db, 'preferencias', uid), { ocultos: l }, { merge: true }).catch((e) => alert(e.message));
  const guardarTodos = (l) => setDoc(doc(db, 'config', 'menu'), { ocultos: l }, { merge: true }).catch((e) => alert(e.message));
  const alternar = (l, p) => (l.includes(p) ? l.filter((x) => x !== p) : [...l, p]);
  const grupos = [...new Set(modulos.map((m) => m.g))];
  return (
    <div className="bus-fondo" onMouseDown={onCerrar}>
      <div className="bus menu-conf" role="dialog" aria-label="Ajustes" onMouseDown={(e) => e.stopPropagation()}>
        <div className="menu-conf-cab">
          <div><b>Ajustes</b><small>{user.displayName || user.email}</small></div>
          <button className="bus-x" onClick={onCerrar}>Cerrar</button>
        </div>
        <div className="menu-conf-lista">
          <div>
            <div className="nav-sec">Avisos en este dispositivo</div>
            <div className="menu-conf-fila">
              <span className="bus-txt"><b>Urgencias de WhatsApp y resultados de laboratorio</b><small>{estadoAv || (avisos ? 'Activados en este dispositivo' : 'Te llegan al móvil u ordenador aunque el panel esté cerrado')}</small></span>
              <label className="switch mini"><input type="checkbox" checked={avisos} onChange={alternarAvisos} /><span className="pista"><span /></span></label>
            </div>
            {esIOS() && !instalada() && <p className="tenue nota" style={{ margin: '.2rem .4rem 0' }}>Instala el panel: Compartir → «Añadir a pantalla de inicio».</p>}
          </div>
          <div>
            <div className="nav-sec">Abrir el panel en</div>
            <div className="menu-conf-fila">
              <span className="bus-txt"><b>Pantalla de inicio</b><small>La que ves nada más entrar</small></span>
              <select value={inicio || ''} onChange={(e) => setDoc(doc(db, 'preferencias', uid), { inicio: e.target.value || null }, { merge: true })}>
                <option value="">{modulos.find((m) => m.path === '/')?.label || 'Por defecto'}</option>
                {modulos.filter((m) => m.path !== '/').map((m) => <option key={m.path} value={m.path}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div className="nav-sec">Menú</div>
            <p className="tenue nota" style={{ margin: '0 .4rem' }}>Esto solo quita cosas de tu menú{esAdmin ? ' (o del de todos)' : ''}; nunca da acceso a nada. Qué puede ver cada persona se decide en «Usuarios y permisos».</p>
          </div>
          {grupos.map((g) => (
            <div key={g}>
              <div className="nav-sec">{g}</div>
              {modulos.filter((m) => m.g === g).map((m) => {
                const global = todos.includes(m.path);
                const visible = !global && !mios.includes(m.path);
                return (
                  <div key={m.path} className={`menu-conf-fila ${visible ? '' : 'off'}`}>
                    <Icono n={m.icono} />
                    <span className="bus-txt"><b>{m.label}</b><small>{global ? 'Quitado del menú de todos' : m.desc}</small></span>
                    {esAdmin && !m.fijo && <label className="menu-conf-todos" title="Quitarlo del menú de todo el mundo"><input type="checkbox" checked={global} onChange={() => guardarTodos(alternar(todos, m.path))} /> Quitar a todos</label>}
                    <label className="switch mini">
                      <input type="checkbox" disabled={m.fijo || global} checked={visible} onChange={() => guardarMios(alternar(mios, m.path))} />
                      <span className="pista"><span /></span>
                    </label>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Número de pruebas/encargos que necesitan que alguien haga algo
function Contador({ clinicas }) {
  const n = usePendientes(clinicas).filter(necesitaAccion).length;
  return n ? <span className="pend-badge menu">{n}</span> : null;
}

function ContadorDia({ clinicas, uid }) {
  const n = useAvisosDia(clinicas, uid).total;
  return n ? <span className="pend-badge menu">{n}</span> : null;
}

function ContadorHosp({ clinicas }) {
  const n = (useColeccion('hospitalizados', clinicas, [['activo', '==', true]]) || []).reduce((a, h) => a + tomasPendientes(h).length, 0);
  return n ? <span className="pend-badge menu">{n}</span> : null;
}

function ContadorIg({ clinicas }) {
  const n = useImpagosIgualas(clinicas);
  return n ? <span className="pend-badge menu">{n}</span> : null;
}
function ContadorVac() {
  const n = usePendientesVacaciones(true);
  return n ? <span className="pend-badge menu">{n}</span> : null;
}

function Aviso({ m, clinicas, uid, esAdmin }) {
  if (m.aviso) return <Contador clinicas={clinicas} />;
  if (m.avisoDia) return <ContadorDia clinicas={clinicas} uid={uid} />;
  if (m.avisoHosp) return <ContadorHosp clinicas={clinicas} />;
  if (m.avisoIg) return <ContadorIg clinicas={clinicas} />;
  if (m.avisoVac && esAdmin) return <ContadorVac />;
  return null;
}

// Logo de la clínica (Mi clínica → logo) o, si no tiene, el del producto (public/logo-blanco.png, versión para fondos oscuros)
export const MENU_DEFECTO = '#1c1930'; // color del menú si la clínica no elige otro
export const esColorClaro = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || ''); if (!m) return false;
  const n = parseInt(m[1], 16), r = n >> 16, g = (n >> 8) & 255, b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 160;
};
function Logo() {
  const [ok, setOk] = useState(true);
  useEffect(() => setOk(true), [infoOrg().logoUrl]);
  const src = infoOrg().logoUrl || (esColorClaro(infoOrg().colorMenu || MENU_DEFECTO) ? '/logo.png' : '/logo-blanco.png');
  return ok ? <img src={src} alt={infoOrg().nombre || MARCA} onError={() => setOk(false)} /> : null;
}

export default function App() {
  const [cerrados, setCerrados] = useState(() => { try { return JSON.parse(localStorage.getItem('vp-menu-cerrados') || '[]'); } catch { return []; } });
  const [user, setUser] = useState(undefined);
  const [perfil, setPerfil] = useState(undefined); // undefined = cargando · null = sin perfil (alta) · objeto = listo
  const [fallo, setFallo] = useState(''); // error de carga (red/permisos): se ofrece reintentar en lugar de mandar al alta
  const [lento, setLento] = useState(false);
  const [intento, setIntento] = useState(0);
  const uidActual = useRef(null);
  const [orgInfo, setOrgInfo] = useState(null);
  const [clinVer, setClinVer] = useState(0);
  const [clinica, setClinica] = useState(GRUPO);
  const ruta = useLocation().pathname;
  const [menu, setMenu] = useState(false);
  const [buscar, setBuscar] = useState(false);
  const [conf, setConf] = useState(false);
  const [mios, setMios] = useState([]);
  const [inicio, setInicio] = useState(undefined);
  const [pp, setPp] = useState({});
  const ir = useNavigate();
  const yaInicio = useRef(false);
  const [todos, setTodos] = useState([]);
  useEffect(() => (user ? onSnapshot(doc(db, 'preferencias', user.uid), (s) => { setMios(s.data()?.ocultos || []); setInicio(s.data()?.inicio || null); setPp({ visto: !!s.data()?.primerosVisto, oculto: !!s.data()?.primerosOculto }); }, () => setInicio(null)) : undefined), [user?.uid]);
  // Pantalla de inicio elegida: solo al entrar, si se abre la raíz
  useEffect(() => {
    if (yaInicio.current || inicio === undefined || !perfil) return;
    yaInicio.current = true;
    // Primera vez de un administrador: a «Primeros pasos»
    if (perfil.rol === 'admin' && !pp.visto && !pp.oculto) {
      setDoc(doc(db, 'preferencias', user.uid), { primerosVisto: true }, { merge: true }).catch(() => {});
      ir('/configurar', { replace: true });
      return;
    }
    const destino = inicio || (perfil.rol === 'admin' ? '/hoy' : null);
    if (destino && ruta === '/') ir(destino, { replace: true });
  }, [inicio, perfil]);
  useEffect(() => { registrarSW(); }, []);
  useEffect(() => { if (user && perfil) refrescarAvisos(user, perfil); }, [user?.uid, !!perfil]);
  useEffect(() => (perfil ? onSnapshot(doc(db, 'config', 'menu'), (s) => setTodos(s.data()?.ocultos || []), () => setTodos([])) : undefined), [!!perfil]);
  useEffect(() => { setMenu(false); window.scrollTo?.(0, 0); }, [ruta]);
  useEffect(() => {
    const k = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setBuscar((b) => !b); } };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, []);

  // AUD-ARR-01: al recargar una URL profunda, la lectura del perfil puede fallar o llegar tarde (token aún no propagado,
  // red lenta). No se manda nunca al alta por un error: se reintenta con espera creciente y, si sigue fallando, se ofrece reintentar.
  const cargarPerfil = async (u) => {
    setFallo('');
    let ultimo;
    for (let i = 0; i < 4; i++) {
      try {
        const snap = await getDoc(doc(db, 'usuarios', u.uid));
        if (uidActual.current !== u.uid) return; // cambió la sesión mientras tanto
        const p = snap.exists() ? snap.data() : null;
        if (p?.org) fijarOrg(p.org, {}); // antes de que nada lea datos
        setPerfil(p);
        if (p && p.rol !== 'admin' && p.clinicas?.length) setClinica(p.clinicas[0]);
        return;
      } catch (e) {
        ultimo = e;
        console.warn('perfil', i, e?.code || e?.message);
        await new Promise((r) => setTimeout(r, 400 * 2 ** i));
        if (i === 1) await u.getIdToken?.(true).catch(() => {});
      }
    }
    if (uidActual.current === u.uid) setFallo(ultimo?.code === 'permission-denied' ? 'No se ha podido comprobar tu acceso.' : 'No se ha podido conectar con el servidor.');
  };
  useEffect(
    () =>
      onAuthStateChanged(auth, async (u) => {
        uidActual.current = u?.uid || null;
        setUser(u);
        setOrgInfo(null); setClinVer(0); setFallo('');
        if (!u) { fijarOrg(null); fijarClinicas([]); return setPerfil(null); }
        setPerfil(undefined);
        await cargarPerfil(u);
      }),
    []
  );
  // Organización y clínicas: en vivo (si se cambian en «Mi clínica», se ve al momento)
  useEffect(() => {
    const org = perfil?.org;
    if (!org) return undefined;
    const a = onSnapshot(doc(db, 'orgs', org), (s) => { fijarOrg(org, s.data() || {}); setOrgInfo(s.data() || {}); }, (e) => { console.error(e); setFallo('No se han podido cargar los datos de la clínica.'); });
    const b = onSnapshot(collection(db, 'clinicas'), (s) => {
      fijarClinicas(s.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => !c.baja).sort((x, y) => (x.orden ?? 99) - (y.orden ?? 99)));
      setClinVer((v) => v + 1);
    }, (e) => { console.error(e); setFallo('No se han podido cargar las clínicas.'); });
    return () => { a(); b(); };
  }, [perfil?.org, intento]);

  const cargando = user === undefined || (user && (perfil === undefined || (perfil?.org && (!orgInfo || !clinVer))));
  useEffect(() => { setLento(false); if (!cargando) return undefined; const t = setTimeout(() => setLento(true), 8000); return () => clearTimeout(t); }, [cargando]);
  const guia = usePasos(!!perfil?.org && perfil?.rol === 'admin' && !!orgInfo);

  if (fallo && user) return <Reintentar texto={fallo} onReintentar={() => { setFallo(''); setOrgInfo(null); setClinVer(0); if (perfil === undefined) cargarPerfil(user); else setIntento((i) => i + 1); }} />;
  if (user === undefined) return <Cargando lento={lento} />;

  if (!user)
    return (
      <div className="entrada">
        <PanelMarca />
        <section className="entrada-form">
          <main className="entrada-caja">
            <img className="entrada-logo-movil" src="/logo.png" alt="" />
            <h1>Bienvenido a {MARCA}</h1>
            {USE_EMULATORS ? (
              <>
                <p>Modo emulador local (datos ficticios). No conecta a Firebase real.</p>
                <LoginEmulador />
              </>
            ) : (
              <>
                <p>Entra con la cuenta de Google de tu clínica.</p>
                <button className="entrada-google" onClick={() => signInWithPopup(auth, google)}>
                  <LogoGoogle />
                  Continuar con Google
                </button>
                <div className="entrada-nuevo"><b>¿Es tu primera vez?</b> Entra igual: crearás tu clínica en un minuto y tendrás 14 días gratis, sin tarjeta.</div>
              </>
            )}
          </main>
          <footer className="entrada-pie">
            <a href="https://vetpanel.es">vetpanel.es</a><a href="/privacidad">Privacidad</a><a href="/condiciones">Condiciones</a>
          </footer>
        </section>
      </div>
    );

  if (perfil === undefined) return <Cargando lento={lento} />;
  if (!perfil) return <Alta user={user} onListo={() => cargarPerfil(user)} />;

  if (!perfil.org)
    return (
      <div className="centro login">
        <h2>Tu usuario no está en ninguna clínica</h2>
        <p className="tenue">{user.email}</p>
        <button className="btn sec" onClick={() => signOut(auth)}>Salir</button>
      </div>
    );

  if (!orgInfo || !clinVer) return <Cargando lento={lento} />;
  if (perfil.rol !== 'admin' && !(perfil.clinicas || []).some((id) => CLINICAS.some((c) => c.id === id)))
    return (
      <div className="centro login" role="alert">
        <h2>Tu usuario todavía no tiene clínica asignada</h2>
        <p className="tenue">Pide a quien administra el panel que te asigne una clínica en «Usuarios y permisos». {user.email}</p>
        <div className="acciones"><button className="btn" onClick={() => window.location.reload()}>Ya está, volver a entrar</button><button className="btn sec" onClick={() => signOut(auth)}>Salir</button></div>
      </div>
    );

  const esAdmin = perfil.rol === 'admin';
  const esEquipo = perfil.rol === 'equipo';
  // Lo que un administrador le ha quitado a esta persona en «Usuarios y permisos» (no aparece ni se puede abrir)
  const modulos = (esEquipo ? MODULOS_EQUIPO : esAdmin ? [...MODULOS, MODULO_ACCESOS, MODULO_MICLINICA] : MODULOS).filter((m) => esAdmin || m.fijo || !(perfil.sinAcceso || []).includes(m.path));
  const visibles = esAdmin ? CLINICAS : CLINICAS.filter((c) => perfil.clinicas?.includes(c.id));
  const clinicasActivas = clinica === GRUPO ? visibles.map((c) => c.id) : [clinica];
  const GRUPO_COLOR = orgInfo.color || GRUPO_COLOR_DEF;
  const color = clinica === GRUPO ? GRUPO_COLOR : CLINICAS.find((c) => c.id === clinica)?.color;
  const activa = orgActiva(orgInfo);
  const quedan = esAdmin ? diasPrueba(orgInfo) : null;

  const esPlataforma = /@vetpanel\.es$/i.test(user.email || '') && import.meta.env.MODE !== 'demo';
  const verGuia = esAdmin && !pp.oculto && guia.hechos < guia.total;
  const enMenu = modulos.filter((m) => m.fijo || (!mios.includes(m.path) && !todos.includes(m.path)));
  const grupos = [...new Set(enMenu.map((m) => m.g))];
  const plegar = (g) => setCerrados((l) => { const n = l.includes(g) ? l.filter((x) => x !== g) : [...l, g]; try { localStorage.setItem('vp-menu-cerrados', JSON.stringify(n)); } catch { /* sin almacenamiento */ } return n; });
  const actual = modulos.find((m) => m.path === ruta);
  const enlace = (m) => (
    <NavLink key={m.path} to={m.path} end>
      <Icono n={m.icono} /><span className="nav-txt">{m.label}</span>{incluye(m.path, orgInfo) ? <Aviso m={m} clinicas={clinicasActivas} uid={user.uid} esAdmin={esAdmin} /> : <span className="nav-candado" title="No incluido en tu plan"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg></span>}
    </NavLink>
  );

  if (ruta === '/configurar' && esAdmin)
    return (
      <Ctx.Provider value={{ user, perfil, esAdmin, esEquipo, clinica, clinicasActivas }}>
        <Suspense fallback={<div className="centro">Cargando…</div>}><Configurar /></Suspense>
      </Ctx.Provider>
    );

  return (
    <Ctx.Provider value={{ user, perfil, esAdmin, esEquipo, clinica, clinicasActivas }}>
      <div className={`layout${esColorClaro(orgInfo.colorMenu || MENU_DEFECTO) ? ' menu-claro' : ''}`} style={{ '--c': color, '--menu': orgInfo.colorMenu || MENU_DEFECTO }}>
        <aside className={menu ? 'abierto' : ''}>
          <div className="aside-top">
            <NavLink to="/" className="marca">
              <Logo />
              <span><b>{orgInfo.nombreCorto || orgInfo.nombre || MARCA}</b></span>
            </NavLink>
            <span className="movil-titulo">{actual?.label || ''}</span>
            <button className="buscar-btn" onClick={() => setBuscar(true)} aria-label="Buscar">
              <Icono n="buscar" /><span>Buscar</span><kbd>⌘K</kbd>
            </button>
          </div>
          <nav>
            {esPlataforma && <NavLink to="/plataforma" className="nav-guia"><Icono n="inicio" /><span className="nav-txt">Plataforma VetPanel</span></NavLink>}
            {verGuia && (
              <NavLink to="/primeros-pasos" className="nav-guia">
                <Icono n="asistente" /><span className="nav-txt">Primeros pasos</span><span className="nav-guia-n">{guia.hechos}/{guia.total}</span>
              </NavLink>
            )}
            {grupos.map((g) => {
              const items = enMenu.filter((m) => m.g === g);
              const abierto = !cerrados.includes(g);
              if (g === 'Hoy') return <div key={g} className="nav-grupo nav-hoy">{items.map(enlace)}</div>; // «Hoy» va solo, sin cabecera
              return (
                <div key={g} className={`nav-grupo${abierto ? '' : ' cerrado'}`}>
                  <button type="button" className="nav-sec nav-plegar" aria-expanded={abierto} onClick={() => plegar(g)}>
                    {g}<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </button>
                  {abierto && items.map(enlace)}
                </div>
              );
            })}
          </nav>
          <div className="usuario">
            {user.photoURL ? <img src={user.photoURL} alt="" referrerPolicy="no-referrer" /> : <span className="ini">{(user.displayName || user.email || '?')[0].toUpperCase()}</span>}
            <div>{user.displayName || user.email}</div>
            <button className="salir" onClick={() => setConf(true)} title="Ajustes" aria-label="Ajustes"><Icono n="ajustes" /></button>
            <button className="salir" onClick={() => signOut(auth)} title="Salir" aria-label="Salir"><Icono n="salir" /></button>
          </div>
        </aside>
        <main>
          {ruta !== '/asistente' && (visibles.length > 1 || esAdmin) && <header className="selector">
            {esAdmin && (
              <button className={clinica === GRUPO ? 'on' : ''} style={{ '--cc': GRUPO_COLOR }} onClick={() => setClinica(GRUPO)}><i />Todas</button>
            )}
            {visibles.map((c) => (
              <button key={c.id} className={clinica === c.id ? 'on' : ''} style={{ '--cc': c.color }} onClick={() => setClinica(c.id)}>
                <i />{c.nombre}
              </button>
            ))}
          </header>}
          {quedan != null && activa && <div className={`aviso-prueba${quedan <= 3 ? ' urgente' : ''}`}>{quedan <= 1 ? 'Tu prueba termina hoy' : `Te quedan ${quedan} días de prueba`}. Sin tarjeta no se cobra nada; al terminar el panel se pausa y tus datos se conservan · <NavLink to="/mi-clinica?tab=suscripcion">Activar suscripción</NavLink></div>}
          {esAdmin && orgInfo.estado === 'impago' && activa && <div className="aviso-prueba urgente">No se ha podido cobrar la suscripción. Tienes unos días de margen: revisa la tarjeta para que el panel no se pause · <NavLink to="/mi-clinica?tab=suscripcion">Revisar pago</NavLink></div>}
          <Avisador clinicas={clinicasActivas} uid={user.uid} esEquipo={esEquipo} />
          <Ayuda />
          <Suspense fallback={<p className="tenue cargando">Cargando…</p>}>
          <Routes>
            {modulos.map((m) => (
              <Route key={m.path} path={m.path} element={<Fallo ruta={m.path}>{!(activa || m.path === '/mi-clinica') ? <SinSuscripcion esAdmin={esAdmin} /> : incluye(m.path, orgInfo) ? m.el : m.path === '/' ? <Redirige a="/hoy" /> : <Mejorar path={m.path} esAdmin={esAdmin} />}</Fallo>} />
            ))}
            {Object.entries(ANTIGUAS).map(([de, a]) => <Route key={de} path={de} element={<Redirige a={a} />} />)}
            {esEquipo && <Route path="/hoy" element={<Redirige a="/" />} />}
            <Route path="/unirse/*" element={<Redirige a="/" />} />
            {esAdmin && <Route path="/primeros-pasos" element={<Fallo ruta="/primeros-pasos"><PrimerosPasos /></Fallo>} />}
            {esPlataforma && <Route path="/plataforma" element={<Fallo ruta="/plataforma"><Plataforma /></Fallo>} />}
            <Route path="*" element={<SinPermiso existe={TODAS_LAS_RUTAS.has(ruta.replace(/\/+$/, '') || '/')} />} />
          </Routes>
          </Suspense>
        </main>
        {/* Móvil: barra de abajo con lo que más se usa + "Más" con todo el menú */}
        <nav className="barra-movil">
          {[...enMenu.filter((m) => m.movil), ...enMenu.filter((m) => !m.movil)].slice(0, 4).map((m) => (
            <NavLink key={m.path} to={m.path} end>
              <span className="bm-ico"><Icono n={m.icono} /><Aviso m={m} clinicas={clinicasActivas} uid={user.uid} esAdmin={esAdmin} /></span>
              <span>{m.movil || m.label.split(' ')[0]}</span>
            </NavLink>
          ))}
          <button className={menu ? 'active' : ''} onClick={() => setMenu(!menu)} aria-expanded={menu}>
            <span className="bm-ico"><Icono n="mas" /></span><span>Más</span>
          </button>
        </nav>
        {menu && <div className="hoja-fondo" onClick={() => setMenu(false)}>
          <div className="hoja" onClick={(e) => e.stopPropagation()}>
            {grupos.map((g) => (
              <div key={g} className="nav-grupo">
                <span className="nav-sec">{g}</span>
                <div className="hoja-rejilla">{enMenu.filter((m) => m.g === g).map(enlace)}</div>
              </div>
            ))}
            <div className="hoja-pie">
              <button className="btn sec hoja-salir" onClick={() => { setMenu(false); setConf(true); }}>Ajustes</button>
              <button className="btn sec hoja-salir" onClick={() => signOut(auth)}>Salir</button>
            </div>
          </div>
        </div>}
        <Buscador abierto={buscar} onCerrar={() => setBuscar(false)} clinicas={clinicasActivas} modulos={enMenu} />
        {conf && <Ajustes modulos={modulos} mios={mios} todos={todos} inicio={inicio} esAdmin={esAdmin} user={user} perfil={perfil} onCerrar={() => setConf(false)} />}
      </div>
    </Ctx.Provider>
  );
}
